import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { MODULES, navFor, type Entitlements } from '../lib/nav'
import type { AccountRole, Role } from '../lib/auth'

/**
 * SEARCH ACROSS THE APP: Ctrl+S (Cmd+S on a Mac), or the Search button in the top bar.
 *
 * Pages come from the menu this person already has (role, modules bought, modules hidden),
 * so nothing is offered that would not open. Records come from the server
 * (modules/search/search.js), which searches only what this role may open in modules the
 * practice has. ↑ ↓ to move, Enter to open, Esc to close.
 */
interface Item { title: string; sub?: string; to: string; pid?: number }
/** One patient from a plain-English search (modules/search/cohort.js). */
interface CohortRow {
  id: number; name: string; active: boolean
  owed: number; overdueDays: number; lastSeen: string | null; openPlanValue: number
}
interface AskResult {
  description: string; rows: CohortRow[]; total: number
  canAnswer: boolean; empty?: boolean; note?: string
  /** Sent back when asking for a written answer, so the server re-runs it rather than
   *  trusting a list of patients posted by the browser. */
  filter: Record<string, unknown>
}
interface Thread { w: string; v: string; to?: string }
interface Group { key: string; label: string; items: Item[] }

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

export default function SearchPalette({ role, accountRole, entitlements }: { role: Role | undefined; accountRole?: AccountRole; entitlements: Entitlements }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [found, setFound] = useState<Group[]>([])
  const [busy, setBusy] = useState(false)
  const [active, setActive] = useState(0)
  /* What the highlighted patient is connected to. Asked for only when a patient row is
     highlighted, and only after a short pause, so arrowing down a list fires nothing. */
  const [threads, setThreads] = useState<{ pid: number; rows: Thread[] } | null>(null)
  /**
   * PLAIN ENGLISH SEARCH. A question long enough to be a question gets an "Ask" row at the
   * top; choosing it sends the WORDS ONLY to Claude, which returns a filter we run here.
   * The written answer is a second, separate button because that one sends patient rows.
   */
  const [ask, setAsk] = useState<AskResult | null>(null)
  const [asking, setAsking] = useState(false)
  const [askErr, setAskErr] = useState('')
  const [said, setSaid] = useState('')
  const [saying, setSaying] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Ctrl+S / Cmd+S everywhere in the staff app, instead of the browser's "save page".
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 's') { e.preventDefault(); setOpen(true) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  useEffect(() => { if (open) { setQ(''); setFound([]); setActive(0); setAsk(null); setAskErr(''); setSaid('') } }, [open])
  useEffect(() => { if (open) setTimeout(() => input.current?.focus(), 0) }, [open])
  // Editing the question puts the old answer away rather than leaving it under new text.
  useEffect(() => { setAsk(null); setAskErr(''); setSaid('') }, [q])

  /**
   * Three words or more reads as a question rather than a name - and only for the roles
   * the server will actually answer. A cohort says who owes the practice what, so
   * /search/ask is admin and data_admin only; showing a practitioner a button that always
   * comes back 403 is worse than not showing it.
   */
  const askable = (role === 'admin' || role === 'data_admin')
    && q.trim().split(/\s+/).filter(Boolean).length >= 3
  const runAsk = async () => {
    setAsking(true); setAskErr(''); setSaid(''); setAsk(null)
    try {
      const r = await api.post<AskResult>('/search/ask', { q: q.trim() })
      setAsk(r.data)
    } catch (e) {
      const m = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
      setAskErr(m || 'That did not work.')
    } finally { setAsking(false) }
  }
  const explain = async () => {
    if (!ask) return
    setSaying(true)
    try {
      const r = await api.post<{ text: string }>('/search/ask/answer', { q: q.trim(), filter: ask.filter })
      setSaid(r.data.text)
    } catch (e) {
      const m = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
      setSaid(m || 'Could not write an answer for that.')
    } finally { setSaying(false) }
  }

  // Records from the server, a moment after typing stops; a slower older answer never replaces a newer one.
  const asked = useRef(0)
  useEffect(() => {
    const text = q.trim()
    if (!open || text.length < 2) { setFound([]); setBusy(false); return }
    const n = ++asked.current
    setBusy(true)
    const t = setTimeout(() => {
      api.get<{ groups: Group[] }>('/search', { params: { q: text } })
        .then((r) => { if (n === asked.current) setFound(r.data.groups) })
        .catch(() => { if (n === asked.current) setFound([]) })
        .finally(() => { if (n === asked.current) setBusy(false) })
    }, 220)
    return () => clearTimeout(t)
  }, [q, open])

  // Pages: this person's own menu, matched on the page, its section and its module.
  const pages = useMemo<Group | null>(() => {
    const text = q.trim().toLowerCase()
    const mine = navFor(role, undefined, entitlements, accountRole)
    const moduleLabel = (k: string) => MODULES.find((m) => m.key === k)?.label ?? ''
    const hits = mine.filter((n) => !text || `${n.label} ${n.section} ${moduleLabel(n.module)}`.toLowerCase().includes(text))
      .slice(0, text ? 8 : 6)
      .map((n) => ({ title: n.label, sub: `${moduleLabel(n.module)}${n.section && n.section !== n.label ? ` · ${n.section}` : ''}`, to: n.to }))
    return hits.length ? { key: 'pages', label: text ? 'Pages' : 'Go to', items: hits } : null
  }, [q, role, accountRole, entitlements])

  const groups = [...(pages ? [pages] : []), ...found]
  const flat = groups.flatMap((g) => g.items)
  const activePid = flat[active]?.pid
  useEffect(() => { setActive(0) }, [q, found.length])
  useEffect(() => { list.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' }) }, [active])
  useEffect(() => {
    if (!activePid) { setThreads(null); return }
    let dropped = false
    const t = setTimeout(() => {
      api.get<{ id: number; threads: Thread[] }>(`/search/patient/${activePid}`)
        .then((r) => { if (!dropped) setThreads({ pid: activePid, rows: r.data.threads || [] }) })
        .catch(() => { if (!dropped) setThreads(null) })
    }, 160)
    return () => { dropped = true; clearTimeout(t) }
  }, [activePid])

  const go = (it: Item | undefined) => { if (!it) return; setOpen(false); navigate(it.to) }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(flat.length - 1, a + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)) }
    else if (e.key === 'Enter') {
      e.preventDefault()
      // A question with nothing picked out of the list asks it, rather than opening the
      // first page that happens to match a word in it.
      if (askable && (e.metaKey || e.ctrlKey || !flat.length)) runAsk()
      else go(flat[active])
    }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false) }
  }

  let i = -1
  return (
    <>
      <button onClick={() => setOpen(true)} title={`Search (${isMac ? '⌘' : 'Ctrl'}+S)`}
        className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-500 transition hover:border-slate-300 hover:bg-white hover:text-slate-800">
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
        <span className="hidden sm:inline">Search</span>
        <kbd className="hidden rounded border border-slate-200 bg-white px-1 font-sans text-[10px] text-slate-400 sm:inline">{isMac ? '⌘' : 'Ctrl'} S</kbd>
      </button>
      {open && (
        <div className="lq-overlay fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 px-4 pt-[12vh]" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false) }}>
          <div role="dialog" aria-modal="true" aria-label="Search" className="lq-dialog w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-200">
            <div className="flex items-center gap-3 border-b border-slate-100 px-4">
              <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-slate-400" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
              <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown} placeholder="Search patients, bookings, pages…"
                role="combobox" aria-expanded={flat.length > 0} aria-controls="lq-search-results" aria-activedescendant={flat.length ? `lq-sr-${active}` : undefined}
                className="h-12 w-full border-0 bg-transparent text-base text-slate-900 outline-none placeholder:text-slate-400" autoComplete="off" />
              {busy && <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500" aria-label="Searching" />}
              <kbd className="shrink-0 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] text-slate-400">Esc</kbd>
            </div>
            <div ref={list} id="lq-search-results" role="listbox" className="max-h-[60vh] overflow-y-auto py-1">
              {/* Ask, offered only once the text reads like a question rather than a name. */}
              {askable && !ask && (
                <button type="button" onClick={runAsk} disabled={asking}
                  className="flex w-full items-center gap-2.5 border-b border-slate-100 px-4 py-2.5 text-left hover:bg-brand-50/60">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-slate-900 text-[10px] font-bold text-white" aria-hidden="true">AI</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-slate-900">
                      {asking ? 'Working it out…' : <>Ask: <span className="font-medium">{q.trim()}</span></>}
                    </span>
                    <span className="block text-xs text-slate-500">
                      Finds a group of patients. Only these words are sent, never any patient's details.
                    </span>
                  </span>
                  {!asking && <kbd className="shrink-0 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] text-slate-400">{isMac ? '⌘' : 'Ctrl'} ↵</kbd>}
                </button>)}

              {askErr && <p role="alert" className="mx-4 my-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">{askErr}</p>}

              {ask && (
                <div className="border-b border-slate-100 px-4 pb-3 pt-2">
                  {/* ⚠️ THE FILTER, IN WORDS. The point is that somebody can see what was
                      actually asked of the database and notice when it is not what they meant. */}
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Looked for</p>
                  <p className="mt-0.5 text-sm text-slate-800">{ask.description}</p>
                  {ask.empty
                    ? <p className="mt-2 text-sm text-slate-500">{ask.note}</p>
                    : <p className="mt-0.5 text-xs text-slate-500">
                        {ask.total} patient{ask.total === 1 ? '' : 's'}
                        {ask.total > ask.rows.length && `, showing the first ${ask.rows.length}`}
                      </p>}

                  {said && <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm leading-relaxed text-slate-800">{said}</p>}
                  {ask.canAnswer && ask.rows.length > 0 && !said && (
                    <button type="button" onClick={explain} disabled={saying}
                      className="mt-2 rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
                      {saying ? 'Writing…' : 'Write me an answer'}
                    </button>)}
                  {ask.canAnswer && ask.rows.length > 0 && !said && (
                    /* ⚠️ Said plainly, because this is the button that sends patient data. */
                    <span className="ml-2 text-[11px] text-slate-400">sends these {ask.rows.length} rows to Anthropic</span>)}
                </div>)}

              {ask && ask.rows.length > 0 && (
                <div className="py-1">
                  <p className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Patients</p>
                  {ask.rows.map((r) => (
                    <button key={r.id} type="button"
                      onClick={() => { setOpen(false); navigate(`/patients/${r.id}`) }}
                      className="flex w-full items-baseline gap-3 px-4 py-2 text-left hover:bg-brand-50">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-slate-900">
                          {r.name}{!r.active && <span className="ml-1.5 text-xs font-normal text-slate-400">no longer active</span>}
                        </span>
                        <span className="block truncate text-xs text-slate-500">
                          {r.lastSeen ? `last in ${r.lastSeen}` : 'never been in'}
                          {r.openPlanValue > 0 && ` · £${Math.round(r.openPlanValue).toLocaleString('en-GB')} unfinished`}
                        </span>
                      </span>
                      {r.owed > 0 && (
                        <span className="shrink-0 text-sm font-semibold tabular-nums text-rose-700">
                          £{Math.round(r.owed).toLocaleString('en-GB')}
                          <span className="ml-1 text-[10px] font-normal text-slate-400">{r.overdueDays}d</span>
                        </span>)}
                    </button>))}
                </div>)}

              {!ask && groups.map((g) => (
                <div key={g.key} className="py-1">
                  <p className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{g.label}</p>
                  {g.items.map((it) => {
                    i += 1
                    const n = i
                    return (
                      <Fragment key={`${g.key}-${n}`}>
                      <button id={`lq-sr-${n}`} data-i={n} role="option" aria-selected={n === active}
                        onMouseMove={() => setActive(n)} onClick={() => go(it)}
                        className={`flex w-full items-center justify-between gap-3 px-4 py-2 text-left ${n === active ? 'bg-brand-50' : ''}`}>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-slate-900">{it.title}</span>
                          {/* The threads below say the same thing with more in it, so the
                              subtitle steps aside rather than repeating the next appointment. */}
                          {it.sub && !(n === active && it.pid && threads?.pid === it.pid && threads.rows.length > 0) &&
                            <span className="block truncate text-xs text-slate-500">{it.sub}</span>}
                        </span>
                        {n === active && <span className="shrink-0 text-xs text-brand-700" aria-hidden="true">Open ↵</span>}
                      </button>
                      {/* What this patient is connected to, under the row you are on. */}
                      {n === active && it.pid && threads?.pid === it.pid && threads.rows.length > 0 && (
                        <dl className="ml-7 border-l-2 border-slate-200 pl-3 pr-4 pb-2 pt-0.5">
                          {threads.rows.map((t) => (
                            <div key={t.w} className="flex gap-2 py-0.5 text-xs">
                              <dt className="w-20 shrink-0 text-slate-400">{t.w}</dt>
                              <dd className="min-w-0 truncate text-slate-600">{t.v}</dd>
                            </div>))}
                        </dl>)}
                      </Fragment>)
                  })}
                </div>))}
              {!ask && q.trim().length >= 2 && !busy && flat.length === 0 && !askable && <p className="px-4 py-6 text-center text-sm text-slate-500">Nothing found for "{q.trim()}".</p>}
              {q.trim().length < 2 && <p className="px-4 pb-3 pt-1 text-xs text-slate-400">Type a name, a booking reference, a supplier or a page, or ask a question like "who owes over £500 and has not been in since March".</p>}
            </div>
          </div>
        </div>)}
    </>
  )
}
